'use client'

import { useRouter } from 'next/navigation'
import { Store } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { loginSchema, safeDisplayNext, type LoginResult } from '@feltos/shared'
import type { z } from 'zod'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { useIsHydrated } from '@/hooks/use-hydrated'
import { request } from '@/lib/request'
import { ÅButton } from './button'

interface ÅLoginFormProps {
  tenantId?: string
  next?: string
  loginTitle: string
  brandName: string
  logoUrl: string | null
}

function LoginForm({
  tenantId,
  next,
  loginTitle,
  brandName,
  logoUrl,
}: ÅLoginFormProps) {
  const router = useRouter()
  const isHydrated = useIsHydrated()
  const form = useForm<
    z.input<typeof loginSchema>,
    object,
    z.output<typeof loginSchema>
  >({
    resolver: zodResolver(loginSchema),
    defaultValues: { employeeNo: '', password: '', tenantId },
  })
  const { errors, isSubmitting } = form.formState

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server')
    try {
      const result = await request<LoginResult>('/api/auth/login', {
        method: 'POST',
        json: values,
        fallback: '登录失败',
      })
      router.replace(safeDisplayNext(next) ?? result.redirect)
      router.refresh()
    } catch (error) {
      form.setError('root.server', {
        type: 'server',
        message: error instanceof Error ? error.message : '登录失败',
      })
      form.resetField('password')
    }
  })

  return (
    <Card className="w-full max-w-sm">
      <CardHeader>
        <div className="grid justify-items-center gap-3 text-center">
          {logoUrl ? (
            <span className="login-mark overflow-hidden">
              <img
                src={logoUrl}
                alt={brandName}
                className="size-full object-cover"
              />
            </span>
          ) : (
            <span className="login-mark">
              <Store aria-hidden className="size-6" />
            </span>
          )}
          <div className="flex flex-col gap-1">
            <h1 className="font-heading text-2xl font-bold tracking-tight">
              {loginTitle}
            </h1>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <form
          action="/api/auth/login"
          method="post"
          className="flex flex-col gap-5"
          onSubmit={submit}
          noValidate
        >
          <FieldGroup>
            <Field data-invalid={Boolean(errors.employeeNo)}>
              <FieldLabel htmlFor="employeeNo">工号</FieldLabel>
              <Input
                id="employeeNo"
                autoComplete="username"
                placeholder="admin"
                aria-invalid={Boolean(errors.employeeNo)}
                {...form.register('employeeNo')}
              />
              <FieldError errors={[errors.employeeNo]} />
            </Field>
            <Field data-invalid={Boolean(errors.password)}>
              <FieldLabel htmlFor="password">密码</FieldLabel>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="至少 6 位"
                aria-invalid={Boolean(errors.password)}
                {...form.register('password')}
              />
              <FieldError errors={[errors.password]} />
            </Field>
          </FieldGroup>
          {errors.root?.server?.message && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.server.message}</AlertDescription>
            </Alert>
          )}
          <ÅButton
            size="lg"
            type="submit"
            disabled={!isHydrated || isSubmitting}
            className="w-full"
          >
            {!isHydrated ? '加载中…' : isSubmitting ? '登录中…' : '登录'}
          </ÅButton>
        </form>
      </CardContent>
    </Card>
  )
}

export { LoginForm as ÅLoginForm }
